// JobPage (job page): the office's page for one job, composed only of catalogued pieces. The header
// (JobHeader, StoryStrip) and the StuckBar; on the left the work, each an Area that's open or a line by
// the state and the viewer (v3's matrix): Job summary (KeyValue, the original in a FormRecord), Visits
// (VisitRow, the board's VisitBlock in its slot), Review
// (ReviewFixList, LineTable's sold, billed and used), Estimate (EstimateOptions), Parts and materials and Invoice (LineTable,
// Totals), Photos and forms (PhotoGroup, FormRecord, PhotoViewer), History (HistoryFeed, Composer); on
// the right Customer (ContactRow), Tasks (TaskRow), Site (SiteBlock, with the app's map of the address in
// its place) and Details (KeyValue). Its dialogs are the job page's (Complete, Cancel, Edit details, Send
// back).
// It draws a JobView (job-view.ts) and holds no data of its own: the app builds the view from the
// server's job read, so what the viewer can't see or do (cost and profit are the owner's) is never in it.
// Before there's a job: loading, failed and not found, each with the way back. Reads only --jobpage-*.
import { useRef, useState, type ReactNode } from 'react';
import { Area } from '../../components/Area/Area.tsx';
import { Button } from '../../components/Button/Button.tsx';
import { Close } from '../../components/Close/Close.tsx';
import { Composer } from '../../components/Composer/Composer.tsx';
import { ContactRow } from '../../components/ContactRow/ContactRow.tsx';
import { ConfirmDialog } from '../../components/Dialog/Dialog.tsx';
import { FormRecord } from '../../components/FormRecord/FormRecord.tsx';
import { HistoryFeed, type HistoryEntry } from '../../components/HistoryFeed/HistoryFeed.tsx';
import { KeyValue } from '../../components/KeyValue/KeyValue.tsx';
import { LineTable } from '../../components/LineTable/LineTable.tsx';
import { Link } from '../../components/Link/Link.tsx';
import { MenuButton } from '../../components/Menu/Menu.tsx';
import { PhotoGroup, PhotoStrip } from '../../components/PhotoGroup/PhotoGroup.tsx';
import { PhotoViewer } from '../../components/PhotoViewer/PhotoViewer.tsx';
import { Segmented } from '../../components/Segmented/Segmented.tsx';
import { SiteBlock } from '../../components/SiteBlock/SiteBlock.tsx';
import { TaskRow } from '../../components/TaskRow/TaskRow.tsx';
import { Totals } from '../../components/Totals/Totals.tsx';
import { VisitLinks, VisitRow } from '../../components/VisitRow/VisitRow.tsx';
import { compareLines } from '../../rows/job-columns.ts';
import { EditDetailsDialog } from '../EditDetailsDialog.tsx';
import { EstimateOptions } from '../EstimateOptions/EstimateOptions.tsx';
import type {
  ActionLink,
  FixControl,
  JobAction,
  JobEntry,
  JobPageWaiting,
  JobView,
  OnJobAction,
  VisitBlockSlot,
} from '../job-view.ts';
import type { JobState } from '../job-words.ts';
import { JobHeader, Sep } from '../JobHeader/JobHeader.tsx';
import { RecordLayout } from '../../patterns/RecordLayout/RecordLayout.tsx';
import { ReviewFixList, type ReviewFix } from '../ReviewFixList/ReviewFixList.tsx';
import { SendBackDialog } from '../SendBackDialog.tsx';
import { StoryStrip } from '../StoryStrip/StoryStrip.tsx';
import { StuckBar } from '../StuckBar/StuckBar.tsx';
import './JobPage.css';

export type JobPageProps = {
  /** The board's VisitBlock for a visit (a slot: the board owns it). */
  visitBlock: VisitBlockSlot;
  /**
   * The job's address on its map, where its point came from, and Navigate (a slot: the app's live map,
   * BL-018). Drawn in Site under the address, in place of its sketch and its Directions.
   */
  siteMap?: ReactNode;
  /** What the person does, for the app to run; the app answers with a new view. */
  onAction: OnJobAction;
} & ({ status?: 'ready'; view: JobView } | (JobPageWaiting & { onRetry?: () => void }));

type AreaId =
  | 'summary'
  | 'visits'
  | 'review'
  | 'estimate'
  | 'parts'
  | 'invoice'
  | 'photos'
  | 'history'
  | 'customer'
  | 'tasks'
  | 'site'
  | 'details';

// v3's matrix: what's open in each state, before the viewer's own
const OPEN: Record<JobState, AreaId[]> = {
  booked: ['visits'],
  onhold: ['visits', 'parts'],
  underway: ['visits'],
  review: ['review', 'photos'],
  unpaid: ['invoice'],
  paid: [],
  cancelled: [],
};
const DONE: JobState[] = ['unpaid', 'paid'];
function openAtFirst(s: JobState, o: JobView['opener']): Record<AreaId, boolean> {
  const open = Object.fromEntries(OPEN[s].map((id) => [id, true])) as Record<AreaId, boolean>;
  open.summary = open.history = open.customer = open.site = true;
  if (!o.reviewer || DONE.includes(s)) open.review = false; // a review this viewer can't act on, or a done one, is a line
  if (s === 'review' && !o.reviewer) open.photos = false;
  if (o.fromVisit && s !== 'cancelled') open.visits = true; // they came from a visit on the board
  return open;
}

export function JobPage(props: JobPageProps) {
  if ('view' in props)
    return (
      <Ready
        view={props.view}
        visitBlock={props.visitBlock}
        siteMap={props.siteMap}
        onAction={props.onAction}
      />
    );
  return <Waiting {...props} />;
}

/** A link from the view: an href goes there, anything else back to the app. */
function ViewLink({ link, act }: { link: ActionLink; act: (a: JobAction) => void }) {
  return link.href ? (
    <Link href={link.href}>{link.label}</Link>
  ) : (
    <Link
      variant={link.isDanger ? 'danger' : undefined}
      onPress={() => act({ type: 'go', id: link.id, ...(link.target ? { target: link.target } : {}) })}
    >
      {link.label}
    </Link>
  );
}

function Ready({
  view: v,
  visitBlock,
  siteMap,
  onAction,
}: {
  view: JobView;
  visitBlock: VisitBlockSlot;
  siteMap?: ReactNode;
  onAction: OnJobAction;
}) {
  const s = v.state;
  const [open, setOpen] = useState(() => openAtFirst(s, v.opener));
  const [hit, setHit] = useState<string | null>(null);
  const [dialog, setDialog] = useState<null | 'complete' | 'cancel' | 'edit' | 'sendback'>(null);
  // each opening of a dialog is one attempt; a retry inside it carries the same number (QA-058)
  const [attempt, setAttempt] = useState(0);
  const openDialog = (d: NonNullable<typeof dialog>) => {
    setAttempt((n) => n + 1);
    setDialog(d);
  };
  const [photo, setPhoto] = useState<string | null>(null);
  const head = useRef<HTMLDivElement>(null);
  const toggle = (id: AreaId) => (o: boolean) => setOpen((x) => ({ ...x, [id]: o }));
  // the page's own dialogs open here; anything else is the app's, which says how it went in the view's notice
  const act = (a: JobAction) => {
    if (a.type === 'go' && a.id === 'edit' && v.side.details.edit) return openDialog('edit');
    if (a.type === 'go' && a.id === 'cancel' && v.dialogs.cancel) return openDialog('cancel');
    if (a.type === 'go' && a.id === 'sendback' && v.review?.sendBackTo) return openDialog('sendback');
    void Promise.resolve(onAction(a)).catch(() => undefined);
  };
  const links = (list: ActionLink[] | undefined) =>
    list?.length ? list.map((l) => <ViewLink key={`${l.id}-${l.target ?? ''}`} link={l} act={act} />) : null;
  // opens an area and takes the reader to it; a jump from the strip leaves the focus to the history row
  const go = (id: AreaId, focus = true) => {
    setOpen((x) => ({ ...x, [id]: true }));
    if (!focus) return;
    requestAnimationFrame(() => {
      const el = document.getElementById(`a-${id}`);
      el?.scrollIntoView({ block: 'start' });
      el?.querySelector<HTMLElement>('.fl-area__toggle')?.focus({ preventScroll: true });
    });
  };
  const n = v.review?.left ?? 0;

  // ---- the top
  const end: ReactNode =
    s === 'review' && v.review ? (
      n ? (
        <Link variant="danger" onPress={() => go('review')}>
          {n} thing{n > 1 ? 's' : ''} to clear
        </Link>
      ) : (
        'ready to complete'
      )
    ) : v.standing.endStrong ? (
      <>
        <b>{v.standing.endStrong}</b>
        {v.standing.end ?? ''}
      </>
    ) : (
      v.standing.end
    );
  const p = v.primary;
  const pressMain = () => {
    if (!p) return;
    if (p.id === 'complete')
      return n ? go('review') : v.dialogs.complete ? openDialog('complete') : act({ type: 'complete' });
    act({ type: 'go', id: p.id });
  };
  const main = (stuck?: boolean) =>
    p ? (
      <Button variant="primary" size={stuck ? 'sm' : undefined} onPress={pressMain}>
        {p.label}
      </Button>
    ) : null;
  const onMenu = (id: string) => act({ type: 'go', id });
  const entries = v.history.entries.map((e) => withExtra(e, setPhoto, act));
  const strip = v.strip.stops.length ? (
    <StoryStrip
      stops={v.strip.stops}
      isWaiting={v.strip.isWaiting}
      total={entries.length}
      onStop={(row) => {
        setHit(row);
        go('history', false);
      }}
      onAll={() => go('history')}
    />
  ) : undefined;

  // ---- the work
  const J = v.summary;
  const summaryArea = J ? (
    <Area
      id="a-summary"
      title="Job summary"
      summary={J.line}
      isOpen={!!open.summary}
      onOpenChange={toggle('summary')}
    >
      {J.items.length ? (
        <KeyValue
          items={J.items.map((x) => ({
            label: x.label,
            value: x.link ? <ViewLink link={x.link} act={act} /> : x.value,
          }))}
        />
      ) : (
        <p className="fl-jobpage__note">Nothing written on it but its job type’s own words.</p>
      )}
      {J.original ? (
        <FormRecord title="Show the original" meta="the summary as it was written">
          <p className="fl-jobpage__original">{J.original}</p>
        </FormRecord>
      ) : null}
    </Area>
  ) : null;

  const V = v.visits.rows;
  const visitsArea = (
    <Area
      id="a-visits"
      title="Visits"
      count={V.length || undefined}
      summary={v.visits.summary}
      actions={links(v.visits.actions)}
      isOpen={!!open.visits}
      onOpenChange={toggle('visits')}
    >
      {V.length ? (
        V.map((x) => (
          <VisitRow
            key={x.id}
            id={`visit-${x.n}`}
            name={`Visit ${x.n}`}
            block={visitBlock(x)}
            what={`${x.day} · ${x.what}`}
            how={x.how}
            instructions={x.instructions}
            cameFrom={!!x.cameFrom}
            defaultOpen={!!x.cameFrom}
            opens={x.times ? 'times and actions' : 'actions'}
          >
            {x.times ? (
              <LineTable
                label={`Visit ${x.n}’s times`}
                columns={[
                  { id: 'tech', label: 'Tech' },
                  { id: 'sent', label: 'Sent' },
                  { id: 'otw', label: 'On the way' },
                  { id: 'arr', label: 'Arrived' },
                  { id: 'stop', label: 'Stopped' },
                  { id: 'on', label: 'On site', align: 'end' },
                ]}
                rows={x.times.map((t) => ({
                  id: t.id,
                  cells: {
                    tech: t.tech,
                    sent: t.sent,
                    otw: t.onTheWay,
                    arr: t.arrived,
                    stop: t.stopped,
                    on: t.onSite,
                  },
                }))}
              />
            ) : null}
            <VisitLinks>{links(x.actions)}</VisitLinks>
          </VisitRow>
        ))
      ) : (
        <p className="fl-jobpage__note">No visits booked on this job yet.</p>
      )}
      {v.visits.onSite ? <p className="fl-jobpage__foot">On site in all: {v.visits.onSite}</p> : null}
    </Area>
  );

  const R = v.review;
  const fixControl = (id: string, f: FixControl | undefined): ReactNode =>
    !f ? null : f.kind === 'note' ? (
      <span className="fl-jobpage__muted">{f.text}</span>
    ) : f.kind === 'links' ? (
      <>
        {f.links.map((l) =>
          l.target?.startsWith('photo:') ? (
            <Link key={l.id} onPress={() => setPhoto(l.target!.slice(6))}>
              {l.label}
            </Link>
          ) : (
            <Link
              key={l.id}
              variant={l.isDanger ? 'danger' : undefined}
              onPress={() => act({ type: 'fix', fixId: id, choice: l.id })}
            >
              {l.label}
            </Link>
          ),
        )}
      </>
    ) : (
      <Segmented
        label={f.label}
        selectedKey={f.selected ?? ''}
        onSelectionChange={(k) => act({ type: 'fix', fixId: id, choice: String(k) })}
        items={f.options}
      />
    );
  const fixRows: ReviewFix[] =
    R?.fixes.map((f, i) => ({
      id: f.id,
      n: i + 1,
      title: f.title,
      detail: f.detail,
      isCleared: f.isCleared,
      fix: fixControl(f.id, f.fix),
    })) ?? [];
  const reviewArea = R ? (
    <Area
      id="a-review"
      title="Review"
      summary={
        <>
          {R.summary.lead ? `${R.summary.lead} ` : ''}
          {R.summary.isBad ? <span className="fl-jobpage__bad">{R.summary.text}</span> : R.summary.text}
        </>
      }
      isOpen={!!open.review}
      onOpenChange={toggle('review')}
    >
      <ReviewFixList
        handback={R.handback}
        fixes={fixRows}
        allClear={R.allClear}
        clearLine={R.clearLine}
        foot={
          R.sendBackTo || R.more?.length ? (
            <>
              {R.sendBackTo ? (
                <>
                  <span className="fl-jobpage__muted">Something wrong on site?</span>
                  <Link onPress={() => openDialog('sendback')}>Send back to {R.sendBackTo}</Link>
                </>
              ) : null}
              {links(R.more)}
            </>
          ) : undefined
        }
      >
        {R.compare ? <LineTable {...compareLines(R.compare)} /> : null}
      </ReviewFixList>
    </Area>
  ) : null;

  const E = v.estimates;
  const estimateArea = E ? (
    <Area
      id="a-estimate"
      title="Estimate"
      count={E.list.length > 1 ? E.list.length : undefined}
      summary={E.summary}
      actions={links(E.actions)}
      isOpen={!!open.estimate}
      onOpenChange={toggle('estimate')}
    >
      {E.list.length ? (
        E.list.map((e) => (
          <div key={e.id}>
            {e.title ? <p className="fl-jobpage__note">{e.title}</p> : null}
            <EstimateOptions options={e.options} foot={e.foot} />
            {e.actions.length ? (
              <p className="fl-jobpage__foot fl-jobpage__strip">{links(e.actions)}</p>
            ) : null}
          </div>
        ))
      ) : (
        <p className="fl-jobpage__note">No estimates on this job yet.</p>
      )}
    </Area>
  ) : null;

  const P = v.parts;
  const partsArea = P ? (
    <Area
      id="a-parts"
      title="Parts and materials"
      count={P.rows.length || undefined}
      summary={P.summary}
      actions={links(P.actions)}
      isOpen={!!open.parts}
      onOpenChange={toggle('parts')}
    >
      <LineTable
        label="The job’s parts and materials"
        columns={[
          { id: 'part', label: 'Part' },
          { id: 'from', label: 'From' },
          ...(P.showCost ? [{ id: 'cost', label: 'Cost', align: 'end' as const }] : []),
          { id: 'stands', label: 'Where it stands' },
        ]}
        rows={P.rows.map((x) => ({
          id: x.id,
          cells: {
            part: x.part,
            from: x.from,
            cost: x.cost ?? '',
            stands: x.actions?.length ? (
              <span className="fl-jobpage__strip">
                {x.stands}
                {links(x.actions)}
              </span>
            ) : (
              x.stands
            ),
          },
        }))}
      />
      {P.note ? <p className="fl-jobpage__note">{P.note}</p> : null}
    </Area>
  ) : null;

  const I = v.invoice;
  const invoiceArea = I ? (
    <Area
      id="a-invoice"
      title="Invoice"
      summary={I.summary}
      actions={links(I.actions)}
      isOpen={!!open.invoice}
      onOpenChange={toggle('invoice')}
    >
      {I.lines.length ? (
        <LineTable
          label={`${I.number}’s lines`}
          columns={[
            { id: 'line', label: 'Line' },
            { id: 'qty', label: 'Qty', align: 'end' },
            { id: 'price', label: 'Price', align: 'end' },
            { id: 'amt', label: 'Amount', align: 'end' },
          ]}
          rows={I.lines.map((l) => ({
            id: l.id,
            cells: {
              line: l.note ? (
                <>
                  {l.label} <span className="fl-jobpage__muted">· {l.note}</span>
                </>
              ) : (
                l.label
              ),
              qty: l.qty,
              price: l.price,
              amt: l.amount,
            },
          }))}
        />
      ) : I.emptyNote ? (
        <p className="fl-jobpage__note">{I.emptyNote}</p>
      ) : null}
      {I.totals.length ? <Totals label={`${I.number}’s totals`} rows={I.totals} /> : null}
      {I.matchNote ? (
        <p className="fl-jobpage__note">
          {I.matchNote.text}
          {I.matchNote.link ? (
            <>
              {' '}
              · <Link onPress={() => go('estimate')}>{I.matchNote.link.label}</Link>
            </>
          ) : null}
        </p>
      ) : null}
      {I.margin ? (
        <p className="fl-jobpage__margin">
          Only you see this · cost <b>{I.margin.cost}</b> ({I.margin.parts}) · gross profit{' '}
          <b>{I.margin.profit}</b>
        </p>
      ) : null}
      {I.others ? <p className="fl-jobpage__note">{I.others}</p> : null}
    </Area>
  ) : null;

  const F = v.photos;
  const photosArea = F ? (
    <Area
      id="a-photos"
      title="Photos and forms"
      summary={
        <span className="fl-jobpage__strip">
          {F.photos.length ? (
            // a photo in the line opens the viewer, not the area
            <span onClick={(e) => e.stopPropagation()}>
              <PhotoStrip photos={F.photos} onOpen={setPhoto} />
            </span>
          ) : null}
          {F.summary}
        </span>
      }
      actions={links(F.actions)}
      isOpen={!!open.photos}
      onOpenChange={toggle('photos')}
    >
      {F.groups.map((g) => (
        <PhotoGroup key={g.id} title={g.title} sub={g.sub} stages={g.stages} onOpen={setPhoto} />
      ))}
      {F.note ? <p className="fl-jobpage__note">{F.note}</p> : null}
      {F.forms.length || F.signatures.length ? (
        <div>
          {F.forms.map((f) => (
            <FormRecord key={f.id} title={f.title} meta={f.meta} defaultOpen={f.defaultOpen}>
              <KeyValue
                density="tight"
                items={f.items.map((x) => ({ label: x.label, value: x.value, bad: x.isBad }))}
              />
            </FormRecord>
          ))}
          {F.signatures.map((g) => (
            <FormRecord key={g.id} title={g.title} meta={g.meta}>
              {g.src ? (
                <img className="fl-jobpage__sig" src={g.src} alt={g.alt} />
              ) : (
                <p className="fl-jobpage__note">{g.alt}</p>
              )}
            </FormRecord>
          ))}
        </div>
      ) : null}
    </Area>
  ) : null;

  const H = v.history;
  const historyArea = (
    <Area
      id="a-history"
      title="History"
      count={entries.length || undefined}
      isOpen={!!open.history}
      onOpenChange={toggle('history')}
    >
      <HistoryFeed
        entries={entries}
        pinned={H.pinned ? withExtra(H.pinned, setPhoto, act) : undefined}
        filters={H.filters}
        today={H.today}
        hitId={hit}
        composer={
          H.modes.length ? (
            <Composer
              modes={H.modes}
              label="Write in the job’s history"
              onSend={(mode, text) => onAction({ type: 'compose', mode, text })}
            />
          ) : undefined
        }
      />
    </Area>
  );

  // ---- the side
  const C = v.side.customer;
  const T = v.side.tasks;
  const S = v.side.site;
  const D = v.side.details;
  const side = (
    <>
      <Area id="a-customer" title="Customer" isOpen={!!open.customer} onOpenChange={toggle('customer')}>
        <p className="fl-jobpage__note">
          {C.note}
          {C.recordLink ? (
            <>
              {C.note ? ' · ' : ''}
              <ViewLink link={C.recordLink} act={act} />
            </>
          ) : null}
        </p>
        {C.contacts.length ? (
          <div>
            {C.contacts.map((c) => (
              <ContactRow
                key={c.id}
                value={c.value}
                note={c.note}
                actions={c.actions.map((a) =>
                  a.href
                    ? { label: a.label, href: a.href }
                    : {
                        label: a.label,
                        onPress: () =>
                          a.action && act({ type: 'go', id: a.action.id, target: a.action.target }),
                      },
                )}
              />
            ))}
          </div>
        ) : null}
      </Area>
      {T ? (
        <Area
          id="a-tasks"
          title="Tasks"
          count={T.rows.length || undefined}
          summary={T.summary}
          actions={T.add ? <ViewLink link={T.add} act={act} /> : undefined}
          isOpen={!!open.tasks}
          onOpenChange={toggle('tasks')}
        >
          {T.rows.length ? (
            <div>
              {T.rows.map((t) => (
                <TaskRow
                  key={t.id}
                  title={t.title}
                  who={t.who}
                  due={t.due}
                  isDone={t.isDone}
                  doneOn={t.doneOn}
                  isDisabled={!T.canChange}
                  onChange={(done) => act({ type: 'task', taskId: t.id, isDone: done })}
                />
              ))}
            </div>
          ) : (
            <p className="fl-jobpage__note">No open tasks on this job.</p>
          )}
        </Area>
      ) : null}
      <Area id="a-site" title="Site" isOpen={!!open.site} onOpenChange={toggle('site')}>
        <SiteBlock
          address={S.address}
          // the map's own Navigate goes by the address's point, so Directions isn't said twice
          directionsHref={siteMap ? undefined : S.directionsHref}
          map={siteMap}
          gettingIn={S.gettingIn}
          atAddress={S.atAddress.map((x) => ({
            label: x.link ? <ViewLink link={x.link} act={act} /> : x.label,
            value: x.value,
          }))}
        />
      </Area>
      <Area
        id="a-details"
        title="Details"
        summary={D.summary}
        actions={D.edit ? <Link onPress={() => openDialog('edit')}>Edit</Link> : undefined}
        isOpen={!!open.details}
        onOpenChange={toggle('details')}
      >
        <KeyValue
          density="tight"
          items={D.items.map((x) => ({
            label: x.label,
            value: x.link ? <ViewLink link={x.link} act={act} /> : x.value,
          }))}
        />
      </Area>
    </>
  );

  return (
    <article className="fl-jobpage" aria-labelledby="job-title">
      <StuckBar
        title={v.title}
        mark={v.standing.mark}
        word={v.standing.word}
        extra={
          s === 'review' && n ? (
            <Link variant="danger" onPress={() => go('review')}>
              {n} to clear
            </Link>
          ) : v.standing.endStrong ? (
            <b>{v.standing.endStrong}</b>
          ) : undefined
        }
        actions={
          <>
            <Link onPress={() => go('history')}>History</Link>
            <Link onPress={() => window.scrollTo({ top: 0 })}>Top</Link>
            {main(true)}
          </>
        }
        watch={head}
      />
      <div ref={head}>
        <JobHeader
          titleId="job-title"
          crumb={
            <>
              <ViewLink link={v.crumb} act={act} />
              <span>› Job {v.number}</span>
              <Sep />
              <Link onPress={() => act({ type: 'go', id: 'copy' })}>Copy link</Link>
            </>
          }
          title={v.title}
          who={
            <>
              {v.customer.link ? (
                <ViewLink link={{ ...v.customer.link, label: v.customer.name }} act={act} />
              ) : (
                v.customer.name
              )}
              <Sep />
              {v.address}
            </>
          }
          standing={{ mark: v.standing.mark, word: v.standing.word, line: v.standing.line, end }}
          main={main()}
          mainOffOnPhone={!!p?.hideOnPhone}
          actions={
            v.menu.length ? (
              <MenuButton
                label={`Job ${v.number}’s actions`}
                sections={v.menu}
                onAction={onMenu}
                placement="bottom end"
              >
                Job actions
              </MenuButton>
            ) : undefined
          }
          close={
            <Close
              placement="inline"
              label="Close the job"
              onPress={() => act({ type: 'go', id: 'close' })}
            />
          }
          strip={strip}
        />
      </div>
      {v.notice ? (
        <p
          className={v.notice.isError ? 'fl-jobpage__bad' : 'fl-jobpage__note'}
          role={v.notice.isError ? 'alert' : 'status'}
        >
          {v.notice.text}
        </p>
      ) : null}
      <RecordLayout
        sideLabel="Customer, site and details"
        main={
          <>
            {summaryArea}
            {visitsArea}
            {reviewArea}
            {estimateArea}
            {partsArea}
            {invoiceArea}
            {photosArea}
            {historyArea}
          </>
        }
        side={side}
      />

      {v.dialogs.complete ? (
        <ConfirmDialog
          isOpen={dialog === 'complete'}
          onClose={() => setDialog(null)}
          title={v.dialogs.complete.title}
          confirmLabel="Complete the job"
          cancelLabel="Not yet"
          failText="It isn’t completed. Nothing changed; try again."
          onConfirm={() => onAction({ type: 'complete', attempt })}
        >
          <p>{v.dialogs.complete.body}</p>
        </ConfirmDialog>
      ) : null}
      {v.dialogs.cancel ? (
        <ConfirmDialog
          isOpen={dialog === 'cancel'}
          onClose={() => setDialog(null)}
          title={v.dialogs.cancel.title}
          confirmLabel="Cancel the job"
          cancelLabel="Keep the job"
          reason={{
            label: 'Why is it cancelled?',
            description: 'For the job’s history. The customer doesn’t see it.',
            placeholder: 'e.g. They went with someone else.',
            missing: 'Say why, so the next person to open the job knows.',
          }}
          onConfirm={(reason) => onAction({ type: 'cancel', reason, attempt })}
        >
          <p>{v.dialogs.cancel.body}</p>
        </ConfirmDialog>
      ) : null}
      {D.edit ? (
        <EditDetailsDialog
          isOpen={dialog === 'edit'}
          onClose={() => setDialog(null)}
          job={v.number}
          details={D.edit.details}
          types={D.edit.types}
          teams={D.edit.teams}
          onSave={(details) => onAction({ type: 'editDetails', details, attempt })}
        />
      ) : null}
      {R?.sendBackTo ? (
        <SendBackDialog
          isOpen={dialog === 'sendback'}
          onClose={() => setDialog(null)}
          tech={R.sendBackTo}
          onSend={(reason, message) => onAction({ type: 'sendBack', reason, message, attempt })}
        />
      ) : null}
      <PhotoViewer
        photos={F?.photos ?? []}
        openId={photo}
        onClose={() => setPhoto(null)}
        onChange={setPhoto}
        {...(F?.canShare
          ? { onShareChange: (photoId: string, shared: boolean) => act({ type: 'share', photoId, shared }) }
          : {})}
      />
    </article>
  );
}

/** Loading, failed or not found: the crumb and × to leave by, and what's known of the job. */
function Waiting({
  status,
  known,
  crumb,
  error,
  onRetry,
  onAction,
}: JobPageWaiting & { onRetry?: () => void; onAction: OnJobAction }) {
  const act = (a: JobAction) => void Promise.resolve(onAction(a)).catch(() => undefined);
  const number = known?.number;
  const title =
    status === 'notfound'
      ? number
        ? `There’s no job ${number}`
        : 'There’s no such job'
      : (known?.title ?? (number ? `Job ${number}` : 'A job'));
  const line =
    status === 'loading'
      ? 'Loading the job…'
      : status === 'failed'
        ? (error ?? 'The job didn’t load.')
        : 'It may have been deleted, or the link is wrong. Search for it, or go back.';
  return (
    <article className="fl-jobpage" aria-labelledby="job-title" aria-busy={status === 'loading'}>
      <JobHeader
        titleId="job-title"
        crumb={
          <>
            <ViewLink link={crumb} act={act} />
            {number ? <span>› Job {number}</span> : null}
          </>
        }
        title={title}
        who={<span role={status === 'failed' ? 'alert' : 'status'}>{line}</span>}
        main={
          status === 'failed' && onRetry ? (
            <Button variant="primary" onPress={onRetry}>
              Try again
            </Button>
          ) : undefined
        }
        close={
          <Close placement="inline" label="Close the job" onPress={() => act({ type: 'go', id: 'close' })} />
        }
      />
    </article>
  );
}

function withExtra(e: JobEntry, open: (id: string) => void, act: (a: JobAction) => void): HistoryEntry {
  const { photos, play, ...rest } = e;
  if (play)
    return {
      ...rest,
      extra: play.href ? (
        <Link href={play.href}>{play.label}</Link>
      ) : (
        <Link onPress={() => act({ type: 'go', id: play.id, target: play.target })}>{play.label}</Link>
      ),
    };
  if (photos?.length)
    return {
      ...rest,
      extra: <Link onPress={() => open(photos[0]!)}>{photos.length > 1 ? 'See them' : 'See it'}</Link>,
    };
  return rest;
}
